import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { CalendarEvent, PencilSquare } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getToday } from "helpers/utils";
import EditEncounterDetailsRequestModal from "./EditEncounterDetailsRequestMOdal";
import { handleFillStepData } from "../config/claimSlice";

// Lookup arrays
const actPriorities = {
  A: "ASAP",
  CR: "callback results",
  CS: "callback for scheduling",
  CSP: "callback placer for scheduling",
  CSR: "contact recipient for scheduling",
  EL: "elective",
  EM: "emergency",
  P: "preop",
  PRN: "as needed",
  R: "routine",
  RR: "rush reporting",
  S: "stat",
  T: "timing critical",
  UD: "use as directed",
  UR: "urgent",
};

const admitSourceList = [
  { enctalltyp_code: "IA", enctalltyp_name: "Immediate Admission" },
  { enctalltyp_code: "EER", enctalltyp_name: "Admission from hospital ER" },
  {
    enctalltyp_code: "EOP",
    enctalltyp_name: "Emergency Admission from hospital outpatient",
  },
  {
    enctalltyp_code: "EGPHC",
    enctalltyp_name:
      "Emergency Admission by referral from government primary healthcare center",
  },
  {
    enctalltyp_code: "EGGH",
    enctalltyp_name:
      "Emergency Admission by referral from general government hospital",
  },
  {
    enctalltyp_code: "EPPHC",
    enctalltyp_name:
      "Emergency Admission by referral from private primary healthcare center",
  },
  {
    enctalltyp_code: "EPH",
    enctalltyp_name: "Emergency Admission by referral from private hospital",
  },
  {
    enctalltyp_code: "EIC",
    enctalltyp_name: "Emergency Admission by insurance company",
  },
  {
    enctalltyp_code: "EWGS",
    enctalltyp_name: "Elective waiting list admission government free Scheme",
  },
  {
    enctalltyp_code: "EWSS",
    enctalltyp_name: "Elective waiting list admission self-payment Scheme",
  },
  {
    enctalltyp_code: "EWIS",
    enctalltyp_name:
      "Elective waiting list admission insurance coverage Scheme",
  },
  {
    enctalltyp_code: "EMBA",
    enctalltyp_name: "Emergency Maternity Birth Admission",
  },
  {
    enctalltyp_code: "PMBA",
    enctalltyp_name: "Planned Maternity Birth Admission",
  },
  { enctalltyp_code: "RECR", enctalltyp_name: "Red crescent" },
  { enctalltyp_code: "WKIN", enctalltyp_name: "Walk-in" },
  { enctalltyp_code: "FMLYM", enctalltyp_name: "Family Member" },
  { enctalltyp_code: "AA", enctalltyp_name: "Already Admitted" },
  { enctalltyp_code: "PVAMB", enctalltyp_name: "Private Ambulance" },
  {
    enctalltyp_code: "AAIC",
    enctalltyp_name: "Already admitted- insurance consumed",
  },
  { enctalltyp_code: "Others", enctalltyp_name: "Others" },
];

const intendLength = {
  ISD: "Intended same day",
  IO: "Intended overnight",
};

const serviceEvent = {
  ICSE: "Initial client service event",
  SCSE: "Subsequent client service event",
};

const encounterClasses = {
  AMB: "Ambulatory",
  EMER: "Emergency",
  HH: "Home Health",
  IMP: "In-Patient",
  SS: "Short Stay",
  VR: "Telemedicine",
  ACU: "Acute",
};

// Default mock data for lists that may be empty in production
const defaultTriageCategoryList = [
  { enctalltyp_code: "1", enctalltyp_name: "Resuscitation" },
  { enctalltyp_code: "2", enctalltyp_name: "Emergency" },
  { enctalltyp_code: "3", enctalltyp_name: "Urgent" },
  { enctalltyp_code: "4", enctalltyp_name: "Semi-Urgent" },
  { enctalltyp_code: "5", enctalltyp_name: "Non-Urgent" },
];

const defaultDepartmentDispositionList = [
  { enctalltyp_code: "ADM", enctalltyp_name: "Admitted" },
  { enctalltyp_code: "DIS", enctalltyp_name: "Discharged" },
  { enctalltyp_code: "DED", enctalltyp_name: "Deceased" },
  { enctalltyp_code: "DOA", enctalltyp_name: "Dead on Arrival" },
  { enctalltyp_code: "TRF", enctalltyp_name: "Transferred" },
];

const defaultDischargeDispositionList = [
  { enctalltyp_code: "home", enctalltyp_name: "Discharged to Home" },
  { enctalltyp_code: "died", enctalltyp_name: "Deceased" },
  {
    enctalltyp_code: "hosp",
    enctalltyp_name: "Transferred to Another Hospital",
  },
  { enctalltyp_code: "snf", enctalltyp_name: "Skilled Nursing Facility" },
  { enctalltyp_code: "rehab", enctalltyp_name: "Rehabilitation Facility" },
];

const defaultReAdmissionList = [
  { enctalltyp_code: "R1", enctalltyp_name: "Readmission within 30 days" },
  { enctalltyp_code: "R2", enctalltyp_name: "Readmission after 30 days" },
  { enctalltyp_code: "NR", enctalltyp_name: "Not a Readmission" },
];

const defaultEmergencyArrivalList = [
  { enctalltyp_code: "AMB", enctalltyp_name: "Ambulance" },
  { enctalltyp_code: "WKI", enctalltyp_name: "Walk-In" },
  { enctalltyp_code: "REF", enctalltyp_name: "Referral" },
  { enctalltyp_code: "TRF", enctalltyp_name: "Transfer" },
];

const Item = ({ title, value, remark }) => (
  <div className="d-flex flex-column">
    <span className="fs-10 text-column fw-bold">{title}</span>
    <span className="fs-10 text-capitalize  text-600">{value}</span>
  </div>
);

const EncounterDetailsRequest = () => {
  const dispatch = useDispatch();
  const {
    claimRequestData,
    stepFormData,
    preauthorizationDiagnosis,
    viewClaimInfo,
  } = useSelector((state) => state.claim);
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims"
  );
  const {
    encounterClass,
    discharegSpeciality,
    serviceTypeListEncounter,
    actPeriortyList,
    triageCategoryList,
    emergencyArrivalList,
    departmentDispositionList,
    lengthOfStayList,
    encounterStatusList,
    adminResourceList,
    dischargeDispositionList,
    reAdmissionList,
    eventTypeList,
    admissionSpeciality,
  } = useSelector((state) => state.preauthorization);

  // Use defaults if Redux lists are empty (common in production if API fails or not loaded)
  const effectiveTriageCategoryList =
    triageCategoryList?.length > 0
      ? triageCategoryList
      : defaultTriageCategoryList;
  const effectiveEmergencyArrivalList =
    emergencyArrivalList?.length > 0
      ? emergencyArrivalList
      : defaultEmergencyArrivalList;
  const effectiveDepartmentDispositionList =
    departmentDispositionList?.length > 0
      ? departmentDispositionList
      : defaultDepartmentDispositionList;
  const effectiveDischargeDispositionList =
    dischargeDispositionList?.length > 0
      ? dischargeDispositionList
      : defaultDischargeDispositionList;
  const effectiveReAdmissionList =
    reAdmissionList?.length > 0 ? reAdmissionList : defaultReAdmissionList;

  const payloadClaim = viewClaimInfo?.payload_json;

  const cliamPreauthorization = claimRequestData?.entry?.find(
    (e) => e.resource?.resourceType === "Claim"
  )?.resource;
  const preauthorizationEncounter = claimRequestData?.entry?.find(
    (e) => e.resource?.resourceType === "Encounter"
  )?.resource;
  const status = viewClaimInfo?.claim_status;

  const isDisabled =
    status?.toLowerCase() === "error" ||
    status?.toLowerCase() === "ready to submit" ||
    status?.toLowerCase() === "in process" ||
    status?.toLowerCase() === "rejected" ||
    status?.toLowerCase() === "validation error" ||
    status?.toLowerCase() === "invalid" ||
    status?.toLowerCase() === "cancelled";
  const getServiceEventTypeCode = (type) => {
    if (!Array.isArray(preauthorizationEncounter?.extension)) return "";

    const serviceEvent = preauthorizationEncounter.extension.find((ext) =>
      ext.url?.includes(type)
    );

    return (
      serviceEvent?.valueCodeableConcept?.coding?.[0]?.display ||
      serviceEvent?.valueCodeableConcept?.coding?.[0]?.code ||
      serviceEvent?.valueDateTime ||
      ""
    );
  };

  const supInfo = useMemo(
    () => viewClaimInfo?.payload_json?.sup_info || {},
    [viewClaimInfo?.payload_json?.sup_info]
  );

  const getVitalSignValue = (type) => {
    const item = cliamPreauthorization?.supportingInfo?.find((entry) =>
      entry?.category?.coding?.some((coding) => coding?.code === type)
    );

    if (item?.valueQuantity) {
      return `${item?.valueQuantity?.value}`;
    } else if (item?.valueDateTime) {
      return moment(item?.valueDateTime).format("DD-MM-YYYY");
    } else if (item?.code) {
      return `${item?.code?.coding?.[0]?.code}`;
    } else {
      return "";
    }
  };

  const getHospitalizationTypeCode = (type) => {
    const serviceEvent =
      preauthorizationEncounter?.hospitalization?.extension?.find((ext) =>
        ext.url?.includes(type)
      );

    return (
      serviceEvent?.valueCodeableConcept?.coding?.[0]?.display ||
      serviceEvent?.valueCodeableConcept?.coding?.[0]?.code ||
      serviceEvent?.valueDateTime ||
      ""
    );
  };

  const encounterTriageData = preauthorizationEncounter?.extension?.find(
    (item) => item?.url?.includes("extension-triageDate")
  )?.valueDateTime;

  const empolyImapct = cliamPreauthorization?.supportingInfo?.find((entry) =>
    entry.category?.coding?.some(
      (coding) => coding.code === "employmentImpacted"
    )
  );

  const [showModal, setShowModal] = useState(false);

  // Get values from form data or default to empty
  const getValue = (field) => {
    const value = stepFormData?.[field];

    if (value === null || value === undefined || value === "") return null;

    // Format dates
    if (field.includes("date") && value instanceof Date) {
      return moment(value).format("DD/MM/YYYY");
    }

    // Format select fields
    if (typeof value === "object" && value !== null) {
      return (
        value.label ||
        value.name ||
        value.enctalltyp_name ||
        value.specialities_name ||
        value.description ||
        ""
      );
    }

    return value;
  };

  useEffect(() => {
    dispatch(
      handleFillStepData({
        check: "admission_date",
        value:
          payloadClaim?.enct_stdate ||
          preauthorizationEncounter?.period?.start ||
          "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "discharge_date",
        value:
          payloadClaim?.enct_eddate ||
          preauthorizationEncounter?.period?.end ||
          "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "triage_date",
        value: payloadClaim?.enct_trgdte || encounterTriageData || "",
      })
    );
    let triageCode =
      payloadClaim?.enct_trgctg ||
      getServiceEventTypeCode("extension-triageCategory") ||
      getValue("triage_category") ||
      "";
    let triageFound = effectiveTriageCategoryList?.find(
      (item) => item?.enctalltyp_code === triageCode
    );
    dispatch(
      handleFillStepData({
        check: "triage_category",
        value:
          triageFound ||
          (triageCode
            ? { enctalltyp_name: triageCode, enctalltyp_code: triageCode }
            : ""),
      })
    );
    let emergArrivalCode =
      payloadClaim?.enct_emrgarvlcde ||
      getServiceEventTypeCode("extension-emergencyArrivalCode") ||
      getValue("emergncy_arrival_code") ||
      "";
    let emergArrivalFound = effectiveEmergencyArrivalList?.find(
      (item) => item?.enctalltyp_code === emergArrivalCode
    );
    dispatch(
      handleFillStepData({
        check: "emergncy_arrival_code",
        value:
          emergArrivalFound ||
          (emergArrivalCode
            ? {
              enctalltyp_name: emergArrivalCode,
              enctalltyp_code: emergArrivalCode,
            }
            : ""),
      })
    );
    let deptDispositionCode =
      payloadClaim?.enct_emrgdeptdisp ||
      getServiceEventTypeCode("extension-emergencyDepartmentDisposition") ||
      getValue("emer_department_disposition") ||
      "";
    let deptDispositionFound = effectiveDepartmentDispositionList?.find(
      (item) => item?.enctalltyp_code === deptDispositionCode
    );
    dispatch(
      handleFillStepData({
        check: "emer_department_disposition",
        value:
          deptDispositionFound ||
          (deptDispositionCode
            ? {
              enctalltyp_name: deptDispositionCode,
              enctalltyp_code: deptDispositionCode,
            }
            : ""),
      })
    );
    dispatch(
      handleFillStepData({
        check: "emergency_service_start_date",
        value: payloadClaim?.enct_emergservst
          ? moment(payloadClaim?.enct_emergservst).format("DD-MM-YYYY")
          : getServiceEventTypeCode("extension-emergencyServiceStart")
            ? moment(
              getServiceEventTypeCode("extension-emergencyServiceStart")
            ).format("DD-MM-YYYY , hh:mm:ss A")
            : getValue("emergency_service_start_date")
              ? moment(getValue("emergency_service_start_date")).format(
                "DD-MM-YYYY , hh:mm:ss A"
              )
              : "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "cause_of_death",
        value:
          preauthorizationDiagnosis?.find(
            (item) =>
              item?.code === payloadClaim?.enct_csedth ||
              item?.code ===
              getServiceEventTypeCode("extension-causeOfDeath") ||
              item?.code === getValue("cause_of_death")
          ) || "",
      })
    );
    let encounterClassCode =
      payloadClaim?.enct_cls ||
      preauthorizationEncounter?.class?.code ||
      getValue("encounter_class") ||
      "";
    let encounterClassFound = encounterClass?.find(
      (item) => item?.act_code === encounterClassCode
    );
    dispatch(
      handleFillStepData({
        check: "encounter_class",
        value:
          encounterClassFound ||
          (encounterClassCode
            ? {
              act_description:
                encounterClasses[encounterClassCode] || encounterClassCode,
            }
            : ""),
      })
    );
    dispatch(
      handleFillStepData({
        check: "encounter_status",
        value:
          encounterStatusList?.find(
            (item) =>
              item?.enctalltyp_code === payloadClaim?.enct_status ||
              item?.enctalltyp_code === preauthorizationEncounter?.status ||
              item?.enctalltyp_code === getValue("encounter_status")
          ) || "",
      })
    );
    let serviceEventTypeCode =
      payloadClaim?.enct_sevnttyp ||
      getServiceEventTypeCode("extension-serviceEventType") ||
      getValue("service_event_type") ||
      "";
    let serviceEventTypeFound = eventTypeList?.find(
      (item) => item?.enctalltyp_code === serviceEventTypeCode
    );
    dispatch(
      handleFillStepData({
        check: "service_event_type",
        value:
          serviceEventTypeFound ||
          (serviceEventTypeCode
            ? {
              enctalltyp_name:
                serviceEvent[serviceEventTypeCode] || serviceEventTypeCode,
            }
            : ""),
      })
    );
    dispatch(
      handleFillStepData({
        check: "care_type",
        value:
          serviceTypeListEncounter?.find(
            (item) =>
              item?.enctalltyp_code === payloadClaim?.enct_serv_typ ||
              item?.enctalltyp_code ===
              preauthorizationEncounter?.serviceType?.coding?.[0]?.code ||
              item?.enctalltyp_code === getValue("care_type")
          ) || "",
      })
    );
    let actPriorityCode =
      payloadClaim?.enct_prior ||
      preauthorizationEncounter?.priority?.coding?.[0]?.code ||
      "";
    let actPriorityFound = actPeriortyList?.find(
      (item) => item?.enctalltyp_code === actPriorityCode
    );
    dispatch(
      handleFillStepData({
        check: "act_priority",
        value:
          actPriorityFound ||
          (actPriorityCode
            ? {
              enctalltyp_name:
                actPriorities[actPriorityCode] || actPriorityCode,
            }
            : ""),
      })
    );
    dispatch(
      handleFillStepData({
        check: "admission_speciality",
        value:
          admissionSpeciality?.find(
            (item) =>
              item.specialities_code === payloadClaim?.enct_hosp_admnspeclty ||
              item?.specialities_code ===
              getHospitalizationTypeCode("extension-admissionSpecialty")
          ) ||
          getValue("admission_speciality") ||
          "",
      })
    );
    let dischargeDispositionCode =
      payloadClaim?.enct_disdisp ||
      preauthorizationEncounter?.hospitalization?.dischargeDisposition
        ?.coding?.[0]?.code ||
      "";
    let dischargeDispositionFound = effectiveDischargeDispositionList?.find(
      (item) => item?.enctalltyp_code === dischargeDispositionCode
    );
    dispatch(
      handleFillStepData({
        check: "discharge_disposition",
        value:
          dischargeDispositionFound ||
          (dischargeDispositionCode
            ? {
              enctalltyp_name: dischargeDispositionCode,
              enctalltyp_code: dischargeDispositionCode,
            }
            : ""),
      })
    );
    let intendLengthCode =
      payloadClaim?.enct_hosp_intdlghsty ||
      getHospitalizationTypeCode("extension-intendedLengthOfStay") ||
      "";
    let intendLengthFound = lengthOfStayList?.find(
      (item) => item?.enctalltyp_code === intendLengthCode
    );
    dispatch(
      handleFillStepData({
        check: "intend_length_stay",
        value:
          intendLengthFound ||
          (intendLengthCode
            ? {
              enctalltyp_name:
                intendLength[intendLengthCode] || intendLengthCode,
            }
            : ""),
      })
    );
    dispatch(
      handleFillStepData({
        check: "discharge_speciality",
        value:
          admissionSpeciality?.find(
            (item) =>
              item?.specialities_code ===
              payloadClaim?.enct_hosp_discgspeclty ||
              item?.specialities_code ===
              getHospitalizationTypeCode("extension-dischargeSpecialty")
          ) || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "encounter_admitsource",
        value:
          admitSourceList?.find(
            (item) =>
              item?.enctalltyp_code === payloadClaim?.enct_adm_srce ||
              item?.enctalltyp_code ===
              preauthorizationEncounter?.hospitalization?.admitSource
                ?.coding?.[0]?.code
          ) || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "empoly_impact_start_date",
        value:
          supInfo["14"]?.empimp?.stdate ||
          empolyImapct?.timingPeriod.start ||
          "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "empoly_impact_end_date",
        value:
          supInfo["14"]?.empimp?.eddate || empolyImapct?.timingPeriod.end || "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "estimated_length_stay",
        value:
          supInfo["13"]?.lgthsty ||
          getVitalSignValue("estimated-Length-of-Stay") ||
          getValue("estimated_length_stay") ||
          "",
      })
    );
    dispatch(
      handleFillStepData({
        check: "last_menstrual_code",
        value:
          supInfo["6"]?.menstprd ||
          getVitalSignValue("last-menstrual-period") ||
          getValue("last_menstrual_code") ||
          "",
      })
    );
  }, [
    dispatch,
    claimRequestData,
    handleFillStepData,
    payloadClaim,
    encounterStatusList,
    encounterClass,
    encounterTriageData,
    discharegSpeciality,
    dischargeDispositionList,
  ]);

  // Define encounter details with conditional display
  const itemDetails = [
    {
      id: 1,
      title: "Triage Category",
      value:
        stepFormData?.triage_category?.enctalltyp_name ||
        getServiceEventTypeCode("extension-triageCategory") ||
        "",
    },
    {
      id: 2,
      title: "Emergency Arrival Code",
      value:
        stepFormData?.emergncy_arrival_code?.enctalltyp_name ||
        getServiceEventTypeCode("extension-emergencyArrivalCode") ||
        "",
    },
    {
      id: 3,
      title: "Emergency Department Disposition",
      value:
        stepFormData?.emer_department_disposition?.enctalltyp_name ||
        getServiceEventTypeCode("extension-emergencyDepartmentDisposition") ||
        "",
    },
    {
      id: 4,
      title: "Emergency Service Date",
      value: getValue("emergency_service_start_date")
        ? moment(getValue("emergency_service_start_date")).format(
          "DD-MM-YYYY , hh:mm:ss A"
        )
        : getServiceEventTypeCode("extension-emergencyServiceStart")
          ? moment(
            getServiceEventTypeCode("extension-emergencyServiceStart")
          ).format("DD-MM-YYYY , hh:mm:ss A")
          : "",
    },

    {
      id: 6,
      title: "Encounter Class",
      value:
        stepFormData?.encounter_class?.act_description ||
        encounterClasses[
        preauthorizationEncounter?.class?.code || payloadClaim?.enct_cls
        ] ||
        preauthorizationEncounter?.class?.code ||
        payloadClaim?.enct_cls ||
        "",
    },
    {
      id: 7,
      title: "Encounter Status",
      value:
        stepFormData?.encounter_status?.enctalltyp_name ||
        encounterStatusList?.find(
          (item) => item?.enctalltyp_code === stepFormData?.encounter_status
        )?.enctalltyp_name ||
        "",
    },
    {
      id: 8,
      title: "Service Event Type",
      value:
        stepFormData?.service_event_type?.enctalltyp_name ||
        serviceEvent[getServiceEventTypeCode("extension-serviceEventType")] ||
        getServiceEventTypeCode("extension-serviceEventType") ||
        "",
    },
    {
      id: 9,
      title: "Service Type",
      value:
        stepFormData?.care_type?.enctalltyp_name ||
        preauthorizationEncounter?.serviceType?.coding?.[0]?.display ||
        preauthorizationEncounter?.serviceType?.coding?.[0]?.code ||
        "",
    },
    {
      id: 9,
      title: "Cause Of Death",
      value:
        stepFormData?.cause_of_death?.description ||
        preauthorizationDiagnosis?.find(
          (item) =>
            item?.code === getValue("cause_of_death") ||
            item?.code === getServiceEventTypeCode("extension-causeOfDeath")
        )?.description ||
        "",
    },
    {
      id: 10,
      title: "Act Priority",
      value:
        getValue("act_priority")?.enctalltyp_name ||
        actPriorities[preauthorizationEncounter?.priority?.coding?.[0]?.code] ||
        preauthorizationEncounter?.priority?.coding?.[0]?.code ||
        "",
    },
    {
      id: 11,
      title: "Admission Speciality",
      value:
        stepFormData?.admission_speciality?.specialities_name ||
        admissionSpeciality?.find(
          (item) =>
            item.specialities_code ===
            getHospitalizationTypeCode("extension-admissionSpecialty")
        )?.specialities_name ||
        "",
    },
    {
      id: 12,
      title: "Discharge Disposition",
      value:
        stepFormData?.discharge_disposition?.enctalltyp_name ||
        preauthorizationEncounter?.hospitalization?.dischargeDisposition
          ?.coding?.[0]?.display ||
        preauthorizationEncounter?.hospitalization?.dischargeDisposition
          ?.coding?.[0]?.code ||
        "",
    },
    {
      id: 13,
      title: "Intend Length Of Stay",
      value:
        stepFormData?.intend_length_stay?.enctalltyp_name ||
        intendLength[
        getHospitalizationTypeCode("extension-intendedLengthOfStay")
        ] ||
        getHospitalizationTypeCode("extension-intendedLengthOfStay") ||
        "",
    },
    {
      id: 14,
      title: "Discharge Speciality",
      value:
        stepFormData?.discharge_speciality?.specialities_name ||
        admissionSpeciality?.find(
          (item) =>
            item?.specialities_code ===
            getHospitalizationTypeCode("extension-dischargeSpecialty")
        )?.specialities_name ||
        "",
    },
    {
      id: 15,
      title: "Admit Source",
      value:
        stepFormData?.encounter_admitsource?.enctalltyp_name ||
        admitSourceList?.find(
          (item) =>
            item?.enctalltyp_code ===
            preauthorizationEncounter?.hospitalization?.admitSource?.coding?.[0]
              ?.code
        )?.enctalltyp_name ||
        preauthorizationEncounter?.hospitalization?.admitSource?.coding?.[0]
          ?.code ||
        "",
    },
    {
      id: 16,
      title: "Employment Impacted Period",
      value: stepFormData?.empoly_impact_start_date
        ? `From ${moment(stepFormData?.empoly_impact_start_date).format(
          "DD-MM-YYYY"
        )} To ${moment(stepFormData?.empoly_impact_end_date).format(
          "DD-MM-YYYY"
        )}`
        : getValue("empoly_impact_start_date") &&
          getValue("empoly_impact_end_date")
          ? `From ${moment(getValue("empoly_impact_start_date")).format(
            "DD-MM-YYYY"
          )} To ${moment(getValue("empoly_impact_end_date")).format(
            "DD-MM-YYYY"
          )}`
          : "",
    },
    {
      id: 17,
      title: "Estimated Length Of Stay",
      value:
        stepFormData?.estimated_length_stay ||
        getValue("estimated_length_stay") ||
        getVitalSignValue("estimated-Length-of-Stay") ||
        "",
    },
    {
      id: 18,
      title: "Last Menstrual Period",
      value: stepFormData?.last_menstrual_code
        ? moment(stepFormData?.last_menstrual_code).format("DD-MM-YYYY")
        : getValue("last_menstrual_code") ||
        getVitalSignValue("last-menstrual-period") ||
        "",
    },
  ].filter((field) => field.value && field.value !== "" && field.title);

  // Group into chunks of 4 for grid layout
  const chunkedDetails = [];
  for (let i = 0; i < itemDetails.length; i += 4) {
    chunkedDetails.push(itemDetails.slice(i, i + 4));
  }

  return (
    <>
      <Card className="mb-4 border border-2 border-info">
        <Card.Header className="d-flex badge-subtle-info justify-content-between border-info border-bottom align-items-center">
          <strong className="fs-12 fw-bold text-info">
            <CalendarEvent className="me-2" /> Encounter Details
          </strong>
          {isDisabled && Number(claimsSetting?.submit) === 1 && (
            <PencilSquare
              onClick={() => setShowModal(true)}
              role="button"
              size={12}
              className="text-info rounded p-0"
            />
          )}
        </Card.Header>
        <Card.Body className="p-2">
          <Row className="">
            {(stepFormData?.admission_date ||
              preauthorizationEncounter?.period?.start) && (
                <Col xs={12} md={4}>
                  <div className="d-flex flex-column">
                    <span className="fw-bold text-800 fs-12">Admission Date</span>
                    <span className="fw-normal text-600 fs-12">
                      {moment(
                        stepFormData?.admission_date ||
                        preauthorizationEncounter?.period?.start
                      ).format("DD-MM-YYYY , hh:mm:ss A")}
                    </span>
                  </div>
                </Col>
              )}
            {(stepFormData?.discharge_date ||
              preauthorizationEncounter?.period?.end) && (
                <Col xs={12} md={4}>
                  <div className="d-flex  flex-column">
                    <span className="fw-bold text-800 fs-12">Discharge Date</span>
                    <span className="fw-normal fs-12 text-600 ">
                      {moment(
                        stepFormData?.discharge_date ||
                        preauthorizationEncounter?.period?.end
                      ).format("DD-MM-YYYY , hh:mm:ss A")}
                    </span>
                  </div>
                </Col>
              )}
            {(stepFormData?.triage_date || encounterTriageData) && (
              <Col xs={12} md={4}>
                <div className="d-flex  flex-column">
                  <span className="fw-bold text-800 fs-12">Triage Date</span>
                  <span className="fw-normal text-600 fs-12">
                    {stepFormData?.triage_date || encounterTriageData
                      ? moment(
                        stepFormData?.triage_date || encounterTriageData
                      ).format("DD-MM-YYYY , hh:mm:ss A")
                      : ""}
                  </span>
                </div>
              </Col>
            )}
          </Row>
          <hr className="my-1" />
          {chunkedDetails.length > 0 ? (
            chunkedDetails.map((row, rowIndex) => (
              <Row key={rowIndex} className="mb-3">
                {row.map((field) => (
                  <Col xs={12} sm={6} md={3} key={field.id}>
                    <div className="text-800 fw-bold fs-12">{field.title}</div>
                    <div className="fw-semibold text-600 fs-12">
                      {field.value}
                    </div>
                  </Col>
                ))}
              </Row>
            ))
          ) : (
            <div className="text-center text-capitalize py-3 fs-11 text-600">
              No encounter details available
            </div>
          )}
        </Card.Body>
      </Card>
      <EditEncounterDetailsRequestModal
        show={showModal}
        onHide={() => setShowModal(false)}
      />
    </>
  );
};

export default EncounterDetailsRequest;
